import { apiClient } from '@/lib/apiClient';
import { useCallback, useEffect, useRef, type MouseEvent } from 'react';

export function useBannerTracking() {
    const viewedBannerIdsRef = useRef<Set<string>>(new Set());

    const trackBannerView = useCallback(async (bannerId: number, placement: string) => {
        const trackingKey = `${bannerId}-${placement}`;

        if (!bannerId || viewedBannerIdsRef.current.has(trackingKey)) {
            return;
        }

        viewedBannerIdsRef.current.add(trackingKey);

        try {
            await apiClient.post(`/api/banners/${bannerId}/view`);
        } catch (error) {
            viewedBannerIdsRef.current.delete(trackingKey);
            console.error('Error tracking banner view:', error);
        }
    }, []);

    const handleBannerClick = useCallback(async (event: MouseEvent, bannerId: number | null, linkUrl: string | null) => {
        if (!linkUrl) {
            event.preventDefault();
            return;
        }

        if (!bannerId) {
            return;
        }

        event.preventDefault();

        try {
            const { data } = await apiClient.post<{ redirect_url?: string }>(`/api/banners/${bannerId}/click`);
            window.location.href = data.redirect_url || linkUrl;
        } catch (error) {
            console.error('Error tracking banner click:', error);
            window.location.href = linkUrl;
        }
    }, []);

    return {
        trackBannerView,
        handleBannerClick,
    };
}

export function useBannerVisibilityTracker<T extends HTMLElement>(
    bannerId: number | null | undefined,
    placement: string,
    onVisible: (bannerId: number, placement: string) => void,
) {
    const elementRef = useRef<T | null>(null);

    useEffect(() => {
        if (!bannerId) {
            return;
        }

        const element = elementRef.current;

        if (!element) {
            return;
        }

        if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
            onVisible(bannerId, placement);
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                if (!entries.some((entry) => entry.isIntersecting)) {
                    return;
                }

                onVisible(bannerId, placement);
                observer.disconnect();
            },
            {
                threshold: 0.5,
            },
        );

        observer.observe(element);

        return () => observer.disconnect();
    }, [bannerId, onVisible]);

    return elementRef;
}
